import { useAuth } from "../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../utils/enums";
import {
  RequestDeskRequest,
  RequestDeskStatus,
  RequestDeskType,
} from "../models/types";

/**
 * Single source of truth for Request Desk UI permissions. Mirrors the backend
 * request_desk_access.ts — the backend is authoritative, this only decides what
 * to render. Every check is (module, action); no role titles anywhere, so access
 * changes are Access Management config, not code.
 */
export interface RequestDeskTab {
  key: string;
  label: string;
}

type Cap =
  | "canCreate"
  | "canApprove"
  | "canTriage"
  | "canExecute"
  | "canViewAll"
  | "canViewFaults";

// Any one listed capability grants the tab (OR) — mirrors the backend
// TAB_REQUIRES map. The dev queue is shared by developers and approvers.
const ALL_TABS: { key: string; label: string; caps: Cap[] }[] = [
  { key: "mine", label: "My requests", caps: ["canCreate"] },
  {
    key: "pending_approval",
    label: "Pending my approval",
    caps: ["canApprove"],
  },
  {
    key: "dev_queue",
    label: "Dev queue",
    caps: ["canExecute", "canTriage", "canViewAll"],
  },
  { key: "all", label: "All requests", caps: ["canViewAll"] },
];

export interface RequestDeskAccess {
  canCreate: boolean;
  canApprove: boolean;
  canTriage: boolean;
  canExecute: boolean;
  canViewAll: boolean;
  canViewFaults: boolean;
  tabs: RequestDeskTab[];
  /** Assignment is an approver action and only on an approved, live request. */
  canAssign: (r: RequestDeskRequest) => boolean;
  /** Execution belongs to the assigned developer alone. */
  canExecuteOn: (r: RequestDeskRequest) => boolean;
  /** Approve/reject is only meaningful while the request awaits approval. */
  canApproveOn: (r: RequestDeskRequest) => boolean;
  /**
   * ERP-176 Phase 2: the automated apply-preview/apply pair is only offered on
   * a Service Request that actually carries a field to change — same gate as
   * canExecuteOn, since it's still "the assigned developer, on an approved,
   * live request."
   */
  canApplyOn: (r: RequestDeskRequest) => boolean;
}

const isTerminal = (r: RequestDeskRequest) =>
  r.status === RequestDeskStatus.resolved ||
  r.status === RequestDeskStatus.rejected ||
  r.status === RequestDeskStatus.closed;

// "not_required" is grandfathered for requests raised before approval became
// universal — mirrors isApprovedForWork() on the backend.
const isApproved = (r: RequestDeskRequest) =>
  r.approval_status === "approved" || r.approval_status === "not_required";

export const useRequestDeskAccess = (): RequestDeskAccess => {
  const { user, isAuthorised } = useAuth();

  const base = {
    canCreate: isAuthorised({
      module: MODULE.request_desk,
      action: [ACTION.CREATE],
    }),
    canApprove: isAuthorised({
      module: MODULE.request_desk_sr_approval,
      action: [ACTION.UPDATE],
    }),
    canTriage: isAuthorised({
      module: MODULE.request_desk_triage,
      action: [ACTION.UPDATE],
    }),
    canExecute: isAuthorised({
      module: MODULE.request_desk_execute,
      action: [ACTION.UPDATE],
    }),
    canViewAll: isAuthorised({
      module: MODULE.request_desk_all,
      action: [ACTION.READ],
    }),
    canViewFaults: isAuthorised({
      module: MODULE.request_desk_fault_dashboard,
      action: [ACTION.READ],
    }),
  };

  const uid = String(user?.uid ?? "");

  return {
    ...base,
    tabs: ALL_TABS.filter((t) => t.caps.some((c) => base[c])).map((t) => ({
      key: t.key,
      label: t.label,
    })),
    canAssign: (r) => base.canTriage && isApproved(r) && !isTerminal(r),
    canExecuteOn: (r) =>
      base.canExecute &&
      isApproved(r) &&
      !isTerminal(r) &&
      !!r.assigned_to &&
      String(r.assigned_to) === uid,
    canApproveOn: (r) =>
      base.canApprove && r.status === RequestDeskStatus.approval_pending,
    canApplyOn: (r) =>
      base.canExecute &&
      isApproved(r) &&
      !isTerminal(r) &&
      !!r.assigned_to &&
      String(r.assigned_to) === uid &&
      r.type === RequestDeskType.service_request &&
      !!r.service_request?.change_requested?.field,
  };
};
